import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  MenuItem,
  Typography,
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
} from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import integrationService from '../../services/integrationService'
import IntegrationConfigForm from '../integrations/IntegrationConfigForm'
import type { IntegrationConfig, IntegrationConfigCreate, IntegrationConfigUpdate } from '../../types/integration'
import { getErrorMessage } from '../../utils/errorHandlers'

const SMS_PROVIDERS = [
  { value: 'truebulksms', label: 'TrueBulkSMS (DLT Portal)' },
  { value: 'fast2sms', label: 'Fast2SMS' },
  { value: 'twilio', label: 'Twilio' },
  { value: 'gupshup', label: 'Gupshup' },
  { value: 'msg91', label: 'MSG91' },
]

const SMSProvidersTab: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar()
  const [configs, setConfigs] = useState<IntegrationConfig[]>([])
  const [loading, setLoading] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingConfig, setEditingConfig] = useState<IntegrationConfig | null>(null)
  const [newProvider, setNewProvider] = useState('')

  const loadConfigs = async () => {
    setLoading(true)
    try {
      const data = await integrationService.getIntegrationsByType('sms', false)
      setConfigs(data.configs || [])
    } catch (err: any) {
      enqueueSnackbar(getErrorMessage(err, 'Failed to load SMS providers'), { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadConfigs()
  }, [])

  const openAddDialog = () => {
    setEditingConfig(null)
    setNewProvider('')
    setDialogOpen(true)
  }

  const openEditDialog = (config: IntegrationConfig) => {
    setEditingConfig(config)
    setNewProvider(config.provider_name)
    setDialogOpen(true)
  }

  const closeDialog = () => {
    setDialogOpen(false)
    setEditingConfig(null)
    setNewProvider('')
  }

  const handleFormSubmit = async (data: IntegrationConfigCreate | IntegrationConfigUpdate) => {
    try {
      if (editingConfig) {
        await integrationService.updateIntegration(editingConfig.id, data as IntegrationConfigUpdate)
        enqueueSnackbar('SMS Provider updated', { variant: 'success' })
      } else {
        await integrationService.createIntegration(data as IntegrationConfigCreate)
        enqueueSnackbar('SMS Provider added', { variant: 'success' })
      }
      closeDialog()
      loadConfigs()
    } catch (err: any) {
      enqueueSnackbar(getErrorMessage(err, 'Failed to save SMS provider'), { variant: 'error' })
    }
  }

  const handleSetPrimary = async (config: IntegrationConfig) => {
    try {
      await integrationService.setAsPrimary(config.id)
      enqueueSnackbar(`${config.provider_name} set as primary SMS gateway`, { variant: 'success' })
      loadConfigs()
    } catch (err: any) {
      enqueueSnackbar(getErrorMessage(err, 'Failed to set primary provider'), { variant: 'error' })
    }
  }

  const handleDelete = async (config: IntegrationConfig) => {
    if (!window.confirm(`Remove ${config.provider_name} provider?`)) return
    try {
      await integrationService.deleteIntegration(config.id)
      enqueueSnackbar('SMS Provider removed', { variant: 'success' })
      loadConfigs()
    } catch (err: any) {
      enqueueSnackbar(getErrorMessage(err, 'Failed to remove provider'), { variant: 'error' })
    }
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="body2" color="text.secondary">
          Configure one or more SMS gateways. The primary gateway is tried first; others act as failover in priority order.
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openAddDialog}>
          Add SMS Provider
        </Button>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Provider</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Primary</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {configs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography color="text.secondary" sx={{ py: 3 }}>
                    No SMS providers configured yet.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              configs.map((config) => (
                <TableRow key={config.id} hover>
                  <TableCell>
                    {SMS_PROVIDERS.find((p) => p.value === config.provider_name)?.label || config.provider_name}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={config.is_active ? 'Active' : 'Inactive'}
                      color={config.is_active ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title={config.is_primary ? 'Primary provider' : 'Set as primary'}>
                      <IconButton size="small" onClick={() => !config.is_primary && handleSetPrimary(config)}>
                        {config.is_primary ? <StarIcon color="warning" /> : <StarBorderIcon />}
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{config.priority}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit credentials">
                      <IconButton size="small" onClick={() => openEditDialog(config)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Remove">
                      <IconButton size="small" onClick={() => handleDelete(config)} color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingConfig ? `Edit ${editingConfig.provider_name}` : 'Add SMS Provider'}</DialogTitle>
        <DialogContent dividers>
          {!editingConfig && (
            <TextField
              select
              label="SMS Provider"
              value={newProvider}
              onChange={(e) => setNewProvider(e.target.value)}
              fullWidth
              sx={{ mb: 3 }}
            >
              {SMS_PROVIDERS.map((p) => (
                <MenuItem key={p.value} value={p.value}>
                  {p.label}
                </MenuItem>
              ))}
            </TextField>
          )}

          {(editingConfig || newProvider) && (
            <IntegrationConfigForm
              serviceType="sms"
              providerName={editingConfig ? editingConfig.provider_name : newProvider}
              initialData={
                editingConfig
                  ? {
                      is_active: editingConfig.is_active,
                      is_primary: editingConfig.is_primary,
                      priority: editingConfig.priority,
                      config: editingConfig.config || editingConfig.config_data || {},
                    }
                  : undefined
              }
              integrationId={editingConfig?.id}
              onSubmit={handleFormSubmit}
              onCancel={closeDialog}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  )
}

export default SMSProvidersTab
